import { useCallback } from 'react';
import { useCampaignEditorContext } from '../contexts/CampaignEditorContext';
import useNotifications from 'core/notifications/useNotifications';
import { useOnsiteCampaignUrlParams } from 'sections/onsite-campaigns/hooks/useOnsiteCampaignUrlParams';
import { useUpsertCampaignMutation } from 'sections/onsite-campaigns/hooks/useUpsertCampaignMutation';
import { useWorkflowForm } from '../tabs/CampaignWorkflowTab/contexts/CampaignWorkflowFormContext';
import { useCampaignSettings } from '../tabs/CampaignWorkflowTab/state/CampaignSettings/hooks/useCampaignSettings';
import { failedRulesToStateValues } from './failedRulesToStateValues';
import { useFailedRulesToStateMapper } from './useFailedRulesToStateMapper';
import { OnsiteCampaignStatus } from 'sections/onsite-campaigns/types';
import { Workflow } from '../domain/Workflow/Workflow';
import { FlowService } from '../domain/Services/FlowService';
import { useWorkFlowCanvas } from '../tabs/CampaignWorkflowTab/contexts/CampaignFlowProvider';
import { CampaignStateToResponseAdapter } from '../infrastructure/Adapters/CampaignStateToResponseAdapter/CampaignStateToResponseAdapter';
import { useCampaignSectionContext } from 'sections/onsite-campaigns/context/CampaignSectionContext';
import { CampaignEditorState } from '../contexts/CampaignEditorState';
import { useFeatureFlags } from 'core/app/AppContext';
import { FlagService } from '../domain/Services/FlagService';

export const useMoveToStatus = () => {
  const { flagEnabled } = useFeatureFlags();
  const { showNotification } = useNotifications();
  const { siteId } = useOnsiteCampaignUrlParams();
  const upsertCampaignMutation = useUpsertCampaignMutation(siteId);

  const { state: campaignEditorState, dispatch } = useCampaignEditorContext();
  const { state: SectionState } = useCampaignSectionContext();
  const { state: CanvasState } = useWorkFlowCanvas();
  const { actions } = useWorkflowForm();
  const { setStatus } = useCampaignSettings();

  const mapper = useFailedRulesToStateMapper();

  const moveToStatus = useCallback(
    (status: OnsiteCampaignStatus, onSuccessCallback?: () => void) => {
      const failedValidation = Workflow.createFromState(
        new FlowService(CanvasState),
        new FlagService(flagEnabled),
        campaignEditorState,
      ).moveToStatus(status);

      /*
        If we have validation errors then we bubble the failures up to state for the UI to handle and
        exit early.
       */
      if (failedValidation?.length) {
        const { errors, validationResults } = failedRulesToStateValues(failedValidation, mapper);

        const mappedValues = errors.reduce((result, item) => {
          const key = Object.keys(item)[0];
          result[key] = item[key];
          return result;
        }, {});
        actions.setErrors(mappedValues);
        actions.setValidationResults(validationResults, 'test');

        return;
      }

      /*
        If we don't have any validation errors then we try to move to the new status.
        If that is successful then we unset any errors and validation issues being displayed
        on the UI.
      */
      const handleSuccess = () => {
        actions.setErrors({});
        actions.setValidationResults([]);

        if (onSuccessCallback) {
          onSuccessCallback();
        }
        setStatus(status);
        dispatch({ type: 'SAVE_CAMPAIGN' });
      };

      const request = new CampaignStateToResponseAdapter(
        SectionState,
        campaignEditorState,
        (campaignEditorState.edit as CampaignEditorState) ?? campaignEditorState,
        CanvasState,
        { isLiveTrendsEnabled: flagEnabled('ld-trends') },
      );

      request.status = status;
      upsertCampaignMutation.mutate(request, handleSuccess);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaignEditorState, CanvasState, showNotification, upsertCampaignMutation],
  );

  return moveToStatus;
};
